/**
 * The offline send queue.
 *
 * iOS gives a PWA no Background Sync, so nothing can upload while the app is
 * closed. The queue therefore holds only *not yet uploaded* captures and
 * flushes hard whenever the app is open — on launch, on regaining
 * connectivity, and immediately after a send.
 *
 * This store is never the source of truth for anything. Safari evicts a PWA's
 * storage after roughly seven days of non-use, so an item sitting here is at
 * risk; that is precisely why we upload aggressively rather than batching.
 */

const DB_NAME = 'familie'
const DB_VERSION = 1
const STORE = 'outbox'

export interface QueuedSend {
  id: string
  spaceId: string
  uploadedBy: string
  /** Client-generated, unique-constrained server-side. Retries cannot duplicate. */
  idempotencyKey: string
  pages: Blob[]
  /** Absent on items queued before this existed; treat those as 'scan'. */
  source?: 'scan' | 'file'
  createdAt: number
  attempts: number
  lastError?: string
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open()
  return new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE, mode)
    const req = fn(t.objectStore(STORE))
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
    t.oncomplete = () => db.close()
  })
}

export async function enqueue(item: QueuedSend): Promise<void> {
  await tx('readwrite', (s) => s.put(item))
}

export async function listQueue(): Promise<QueuedSend[]> {
  const all = await tx<QueuedSend[]>('readonly', (s) => s.getAll() as IDBRequest<QueuedSend[]>)
  return all.sort((a, b) => a.createdAt - b.createdAt)
}

export async function dequeue(id: string): Promise<void> {
  await tx('readwrite', (s) => s.delete(id))
}

export async function markAttempt(id: string, error: string): Promise<void> {
  const item = await tx<QueuedSend | undefined>('readonly', (s) => s.get(id) as IDBRequest<QueuedSend | undefined>)
  if (!item) return
  await enqueue({ ...item, attempts: item.attempts + 1, lastError: error })
}

/**
 * Anything older than this has almost certainly failed for a reason retrying
 * will not fix. It stays in the queue — we never silently drop a parent's
 * document — but the admin gets told, so the family is not quietly relying on
 * something that never arrived.
 */
export const STALE_AFTER_MS = 24 * 60 * 60 * 1000

export function isStale(item: QueuedSend): boolean {
  return Date.now() - item.createdAt > STALE_AFTER_MS
}
